import { Button, Input, Text } from '@cretesuite/design-system'
import { IonSpinner } from '@ionic/react'
import { msg } from '@lingui/core/macro'
import { useLingui } from '@lingui/react'
import { DateTime } from 'luxon'
import { useState } from 'react'
import { useHistory } from 'react-router-dom'
import { initiateAuth } from '../../helpers/cognitoLogin'
import { useToast } from '../../hooks/useToast'
import { AuthLayout } from './AuthLayout'

function utf8ToB64(str: string | number | boolean) {
  return window.btoa(unescape(encodeURIComponent(str)))
}

export const EnterPhonePage = () => {
  const history = useHistory()
  const [presentToast] = useToast()
  const { _ } = useLingui()

  const [phone, setPhone] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  /** Format raw digits as (XXX) XXX-XXXX */
  const formatPhone = (value: string): string => {
    const digits = value.replace(/\D/g, '').slice(0, 10)
    if (digits.length <= 3) return digits
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
  }

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPhone(formatPhone(e.target.value))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    const cleaned = phone.replace(/\D/g, '')
    if (cleaned.length !== 10) {
      setError(_(msg`Please enter a valid 10-digit phone number`))
      return
    }

    setLoading(true)
    try {
      const expires = Math.floor(
        DateTime.utc().plus({ minutes: 10 }).toSeconds(),
      )

      const fullPhone = `+1${cleaned}`
      const authResp = await initiateAuth(fullPhone)
      const username = authResp.ChallengeParameters.USERNAME || fullPhone

      const qs = new URLSearchParams({
        x: expires.toString(),
        u: utf8ToB64(username),
        s: utf8ToB64(authResp.Session),
      })

      history.push(`/validate?${qs.toString()}`)
    } catch (err) {
      // Keep the toast generic — never surface raw backend/error text to the
      // user. Log the detail for diagnostics instead.
      console.error('Phone number submission failed:', err)
      presentToast({
        color: 'danger',
        message: _(msg`An error occurred, please try again`),
        duration: 4000,
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <AuthLayout id='login-start'>
      <form onSubmit={handleSubmit}>
        {error && (
          <Text variant='body' className='auth-error'>
            {error}
          </Text>
        )}

        <div className='auth-field'>
          <Text variant='label' className='auth-label'>
            {_(msg`Phone number`)}
          </Text>
          <Input
            type='tel'
            inputMode='tel'
            placeholder='(555) 123-4567'
            value={phone}
            onChange={handlePhoneChange}
            autoComplete='tel-national'
            aria-label={_(msg`Phone number`)}
            data-testid='login-phone-input'
          />
        </div>

        <Button
          variant='primary'
          size='lg'
          type='submit'
          disabled={loading}
          className='auth-button'
          data-testid='login-phone-submit'
        >
          {loading ? <IonSpinner name='crescent' /> : _(msg`Continue`)}
        </Button>

        <Text variant='body' className='auth-sms-notice'>
          {_(
            msg`By continuing, you will receive an SMS for verification. Message and data rates may apply.`,
          )}
        </Text>
      </form>
    </AuthLayout>
  )
}

export default EnterPhonePage
